import React, { useState, useEffect, useContext } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import languages from '../languages.js'
import {
  GET_USER_PROFILE_QUERY, UPDATE_USER_PROFILE_MUTATION,
  SET_NATIVE_LANGUAGES_MUTATION, SET_LEARNING_LANGUAGE_MUTATION, REMOVE_LEARNING_LANGUAGE_MUTATION,
} from '../query/gqlQueries.js'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'

// The first-run nudge opens this dialog by itself. It says so here, so the
// dialog can explain that everything is optional and offer a plain way out.
// Read once per open, then cleared.
let openedByOnboarding = false
export const markProfileOnboarding = () => { openedByOnboarding = true }
const wasOnboarding = () => openedByOnboarding

const GENDERS = ['female', 'male', 'prefer not to say']
const LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2']
const nameOf = (code) => languages[code] || code

const parseProfileJson = (raw) => {
  try { const o = JSON.parse(raw || '{}'); return o && typeof o === 'object' ? o : {} } catch { return {} }
}

export default function ProfileDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)
  const isOpen = dialogStates['dialogProfile']

  const [form, setForm] = useState({ name: '', gender: '', birthYear: '', occupation: '', homeCity: '', homeCountry: '' })
  const [hobbies, setHobbies] = useState(['', '', ''])
  const [goals, setGoals] = useState('')
  const [nativeCodes, setNativeCodes] = useState([])   // string[]
  const [learning, setLearning] = useState([])         // [{code,name,level}]
  const [addCode, setAddCode] = useState('tur')
  const [addLevel, setAddLevel] = useState('A0')
  const [status, setStatus] = useState('idle')         // idle | saving | saved | error
  const [errorMsg, setErrorMsg] = useState('')

  const { data, refetch } = useQuery(GET_USER_PROFILE_QUERY, { variables: {}, skip: !isOpen || !username, errorPolicy: 'ignore' })
  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE_MUTATION)
  const [setNativeLanguages] = useMutation(SET_NATIVE_LANGUAGES_MUTATION)
  const [setLearningLanguage] = useMutation(SET_LEARNING_LANGUAGE_MUTATION)
  const [removeLearningLanguage] = useMutation(REMOVE_LEARNING_LANGUAGE_MUTATION)

  // Hydrate the form when the profile loads.
  useEffect(() => {
    const p = data?.getUserProfile
    if (!p) return
    const u = p.user || {}
    setForm({
      name: u.name || '', gender: u.gender || '', birthYear: u.birthYear ? String(u.birthYear) : '',
      occupation: u.occupation || '', homeCity: u.homeCity || '', homeCountry: u.homeCountry || '',
    })
    const pj = parseProfileJson(u.profile)
    const hb = Array.isArray(pj.hobbies) ? pj.hobbies : []
    setHobbies([hb[0] || '', hb[1] || '', hb[2] || ''])
    setGoals(pj.goals || '')
    setNativeCodes((p.native || []).map(n => n.code))
    setLearning((p.learning || []).map(l => ({ code: l.code, name: l.name || nameOf(l.code), level: l.level || 'A0' })))
  }, [data])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const toggleNative = (code) => setNativeCodes(cs => cs.includes(code) ? cs.filter(c => c !== code) : [...cs, code])

  const applyProfile = (p) => {
    if (!p) return
    setNativeCodes((p.native || []).map(n => n.code))
    setLearning((p.learning || []).map(l => ({ code: l.code, name: l.name || nameOf(l.code), level: l.level || 'A0' })))
  }

  const addLearning = async () => {
    if (!addCode) return
    try {
      const res = await setLearningLanguage({ variables: { username, code: addCode, name: nameOf(addCode), level: addLevel } })
      applyProfile(res.data?.setLearningLanguage)
    } catch (e) { setErrorMsg(e.message) }
  }
  const changeLevel = async (code, level) => {
    setLearning(ls => ls.map(l => l.code === code ? { ...l, level } : l))
    try { await setLearningLanguage({ variables: { username, code, name: nameOf(code), level } }) }
    catch (e) { setErrorMsg(e.message) }
  }
  const dropLearning = async (code) => {
    try {
      const res = await removeLearningLanguage({ variables: { username, code } })
      applyProfile(res.data?.removeLearningLanguage)
    } catch (e) { setErrorMsg(e.message) }
  }

  const handleSave = async () => {
    setStatus('saving'); setErrorMsg('')
    try {
      const profile = JSON.stringify({ hobbies: hobbies.map(h => h.trim()).filter(Boolean), goals: goals.trim() })
      await updateUserProfile({
        variables: {
          username,
          input: {
            name: form.name.trim() || null,
            gender: form.gender || null,
            birthYear: form.birthYear ? parseInt(form.birthYear, 10) : null,
            occupation: form.occupation.trim() || null,
            homeCity: form.homeCity.trim() || null,
            homeCountry: form.homeCountry.trim() || null,
            profile,
          },
        },
      })
      await setNativeLanguages({ variables: { username, languages: nativeCodes.map(c => ({ code: c, name: nameOf(c) })) } })
      setStatus('saved')
      refetch()
      setTimeout(() => setStatus('idle'), 1500)
    } catch (e) { setStatus('error'); setErrorMsg(e.message) }
  }

  if (!isOpen) return null
  // Opened by the first-run nudge rather than by the learner: say plainly that
  // it is optional, and offer a way out that isn't just the ✕.
  const onboarding = wasOnboarding()
  const learningCodes = learning.map(l => l.code)

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={() => closeDialog('dialogProfile')}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()} style={{ maxWidth: '720px' }}>
        <button className={styles.closeBtn} onClick={() => closeDialog('dialogProfile')}><CloseIcon /></button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Profile</div>
            <h2 className={styles.title}>{onboarding ? 'Welcome — tell us a little, if you like' : 'About you'}</h2>
          </div>
        </div>

        <div className={styles.container}>
          <p style={{ color: 'var(--frost-muted)', fontSize: '0.88rem', margin: '0 0 0.4rem' }}>
            {onboarding
              ? 'Every field here is optional. What you add is woven into your practice — examples use your name, your city, the things you enjoy — and it lets you drill your own introduction. Share as little or as much as you like, and you can change it any time from Profile.'
              : 'These make practice personal — the app uses them in examples and lets you drill your own introduction.'}
          </p>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div className={styles.field} style={{ flex: 2, minWidth: 180 }}>
              <div className={styles.fieldLabel}>Name</div>
              <input className={styles.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="your first name" />
            </div>
            <div className={styles.field} style={{ flex: 1, minWidth: 130 }}>
              <div className={styles.fieldLabel}>Gender</div>
              <select className={styles.input} value={form.gender} onChange={e => set('gender', e.target.value)}>
                <option value="">—</option>
                {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className={styles.field} style={{ flex: 1, minWidth: 110 }}>
              <div className={styles.fieldLabel}>Birth year</div>
              <input className={styles.input} type="number" value={form.birthYear} onChange={e => set('birthYear', e.target.value)} placeholder="1990" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div className={styles.field} style={{ flex: 2, minWidth: 180 }}>
              <div className={styles.fieldLabel}>Occupation / status</div>
              <input className={styles.input} value={form.occupation} onChange={e => set('occupation', e.target.value)} placeholder="photographer · student · retired…" />
            </div>
            <div className={styles.field} style={{ flex: 1, minWidth: 130 }}>
              <div className={styles.fieldLabel}>City</div>
              <input className={styles.input} value={form.homeCity} onChange={e => set('homeCity', e.target.value)} placeholder="Lisbon" />
            </div>
            <div className={styles.field} style={{ flex: 1, minWidth: 130 }}>
              <div className={styles.fieldLabel}>Country</div>
              <input className={styles.input} value={form.homeCountry} onChange={e => set('homeCountry', e.target.value)} placeholder="Portugal" />
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Three things you like doing (outside work / study)</div>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              {hobbies.map((h, i) => (
                <input key={i} className={styles.input} style={{ flex: 1, minWidth: 150 }} value={h}
                  onChange={e => setHobbies(hs => hs.map((x, j) => j === i ? e.target.value : x))} placeholder={`activity ${i + 1}`} />
              ))}
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Why are you learning? (optional)</div>
            <input className={styles.input} value={goals} onChange={e => setGoals(e.target.value)} placeholder="travel, family, work…" />
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Mother tongue(s)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {Object.keys(languages).map(code => (
                <button key={code} type="button"
                  onClick={() => toggleNative(code)}
                  style={{
                    padding: '0.3rem 0.7rem', borderRadius: 999, cursor: 'pointer', fontSize: '0.82rem',
                    border: `1px solid ${nativeCodes.includes(code) ? 'var(--frost-action)' : 'var(--frost-border2)'}`,
                    background: nativeCodes.includes(code) ? 'var(--frost-right-tint)' : 'transparent',
                    color: nativeCodes.includes(code) ? 'var(--frost-right)' : 'var(--frost-muted)',
                  }}>{languages[code]}</button>
              ))}
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Learning (level is per language)</div>
            {learning.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' }}>
                {learning.map(l => (
                  <div key={l.code} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'var(--frost-field)', border: '1px solid var(--frost-border)', borderRadius: 10, padding: '0.4rem 0.7rem' }}>
                    <span style={{ flex: 1, fontWeight: 600 }}>{l.name}</span>
                    <select className={styles.input} style={{ width: 90 }} value={l.level} onChange={e => changeLevel(l.code, e.target.value)}>
                      {LEVELS.map(lv => <option key={lv} value={lv}>{lv}</option>)}
                    </select>
                    <button type="button" onClick={() => dropLearning(l.code)} style={{ background: 'none', border: 'none', color: 'var(--frost-wrong)', cursor: 'pointer', fontSize: '0.85rem' }}>remove</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <select className={styles.input} style={{ flex: 1, minWidth: 150 }} value={addCode} onChange={e => setAddCode(e.target.value)}>
                {Object.keys(languages).filter(c => !learningCodes.includes(c)).map(c => <option key={c} value={c}>{languages[c]}</option>)}
              </select>
              <select className={styles.input} style={{ width: 90 }} value={addLevel} onChange={e => setAddLevel(e.target.value)}>
                {LEVELS.map(lv => <option key={lv} value={lv}>{lv}</option>)}
              </select>
              <button type="button" onClick={addLearning} style={{ padding: '0.55rem 1rem', borderRadius: 8, border: '1px solid var(--frost-border2)', background: 'transparent', color: 'var(--frost-right)', cursor: 'pointer' }}>+ Add</button>
            </div>
          </div>

          {errorMsg && <div style={{ color: 'var(--frost-wrong)', fontSize: '0.85rem' }}>{errorMsg}</div>}

          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.3rem' }}>
            <button className={styles.input} style={{ cursor: 'pointer', flex: 1, padding: '0.75rem', background: 'var(--frost-action)', color: 'var(--frost-on-action)', fontWeight: 600, border: 'none' }}
              onClick={handleSave} disabled={status === 'saving'}>
              {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved ✓' : 'Save profile'}
            </button>
            {/* A plain way out of the first-run nudge. Skipping is a real
                choice, not something to be nagged about — it never reappears. */}
            {onboarding && (
              <button className={styles.input} style={{ cursor: 'pointer', padding: '0.75rem 1.1rem', background: 'transparent', border: '1px solid var(--frost-border2)', color: 'var(--frost-muted)', fontWeight: 500 }}
                onClick={() => closeDialog('dialogProfile')}>
                Not now
              </button>
            )}
          </div>
          {onboarding && (
            <p style={{ color: '#6b6b76', fontSize: '0.78rem', margin: '0.1rem 0 0', textAlign: 'center' }}>
              You can fill this in later from Profile, and change or clear anything you add.
            </p>
          )}
        </div>
      </div>
    </div>, document.body
  )
}
